/**
 * AgentPalette - Browsable library of available agents
 * 
 * Features:
 * - Categorized agent list
 * - Search/filter functionality
 * - Drag-and-drop to canvas
 * - Agent details on hover
 */

import React, { useState, useMemo } from 'react';
import { useDraggable } from '@dnd-kit/core';

interface Agent {
  id: string;
  name: string;
  description: string;
  category: 'data' | 'ai' | 'integration' | 'notification' | 'control' | 'validation' | 'compliance' | 'security' | 'privacy' | 'deployment' | 'testing' | 'analysis' | 'automation' | 'monitoring' | 'optimization' | 'business';
  icon: string;
  color: string;
  estimatedCost: number;
  avgDuration: number;
  successRate: number;
  capabilities?: string[];
  pricing?: {
    type: string;
    amount: number;
    currency: string;
  };
}

interface AgentPaletteProps {
  agents: Agent[];
  onAgentSelect?: (agent: Agent) => void;
}

export const AgentPalette: React.FC<AgentPaletteProps> = ({ agents, onAgentSelect }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const categories: Record<string, { title: string; description: string; color: string }> = {
    data: {
      title: 'Data Operations',
      description: 'Extract, transform, validate, and load data',
      color: '#3b82f6'
    },
    ai: {
      title: 'AI & Analysis',
      description: 'Pattern analysis, code generation, intelligence',
      color: '#3b82f6'
    },
    validation: {
      title: 'Data Validation',
      description: 'Ensure data quality and correctness',
      color: '#10b981'
    },
    integration: {
      title: 'Integration',
      description: 'Connect external systems and APIs',
      color: '#8b5cf6'
    },
    notification: {
      title: 'Notifications',
      description: 'Alerts, emails, and messaging',
      color: '#8b5cf6'
    },
    control: {
      title: 'Control Flow',
      description: 'Workflow control and orchestration',
      color: '#8b5cf6'
    },
    compliance: {
      title: 'Compliance',
      description: 'Audit, standards, and regulatory compliance',
      color: '#10b981'
    },
    security: {
      title: 'Security',
      description: 'Threat detection, vulnerability assessment',
      color: '#ef4444'
    },
    privacy: {
      title: 'Privacy & Data Governance',
      description: 'GDPR, privacy impact, data governance',
      color: '#8b5cf6'
    },
    deployment: {
      title: 'Deployment',
      description: 'Infrastructure provisioning and deployment',
      color: '#f59e0b'
    },
    testing: {
      title: 'Testing',
      description: 'Quality assurance and test orchestration',
      color: '#10b981'
    },
    analysis: {
      title: 'Analysis',
      description: 'Code analysis, pattern harvesting',
      color: '#3b82f6'
    },
    automation: {
      title: 'Automation',
      description: 'Workflow orchestration and automation',
      color: '#8b5cf6'
    },
    monitoring: {
      title: 'Monitoring',
      description: 'Health checks and system monitoring',
      color: '#14b8a6'
    },
    optimization: {
      title: 'Optimization',
      description: 'Cost and performance optimization',
      color: '#06b6d4'
    },
    business: {
      title: 'Business Intelligence',
      description: 'Market research, competitive analysis',
      color: '#ec4899'
    }
  };

  // Filter agents
  const filteredAgents = useMemo(() => {
    return agents.filter(agent => {
      const matchesSearch = agent.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        agent.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = !selectedCategory || agent.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [agents, searchTerm, selectedCategory]);

  // Group by category
  const groupedAgents = useMemo(() => {
    return filteredAgents.reduce((acc, agent) => {
      if (!acc[agent.category]) {
        acc[agent.category] = [];
      }
      acc[agent.category].push(agent);
      return acc;
    }, {} as Record<string, Agent[]>);
  }, [filteredAgents]);

  return (
    <div className="agent-palette">
      {/* Search */}
      <div className="palette-search">
        <input
          type="text"
          placeholder="Search agents..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
      </div>

      {/* Category filters */}
      <div className="category-filters">
        <button
          onClick={() => setSelectedCategory(null)}
          className={!selectedCategory ? 'active' : ''}
        >
          All
        </button>
        {Object.entries(categories).map(([key, cat]) => (
          <button
            key={key}
            onClick={() => setSelectedCategory(key)}
            className={selectedCategory === key ? 'active' : ''}
            style={{ borderLeftColor: cat.color }}
          >
            {cat.title}
          </button>
        ))}
      </div>

      {/* Agent list */}
      <div className="agent-list">
        {Object.entries(groupedAgents).map(([category, categoryAgents]) => (
          <div key={category} className="agent-category">
            <div className="category-header">
              <h3 style={{ color: categories[category as keyof typeof categories].color }}>
                {categories[category as keyof typeof categories].title}
              </h3>
              <p>{categories[category as keyof typeof categories].description}</p>
            </div>

            <div className="category-agents">
              {categoryAgents.map(agent => (
                <DraggableAgentCard
                  key={agent.id}
                  agent={agent}
                  onSelect={onAgentSelect}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Empty state */}
      {filteredAgents.length === 0 && (
        <div className="empty-state">
          <p>No agents found matching your search</p>
        </div>
      )}
    </div>
  );
};

// Draggable agent card
const DraggableAgentCard: React.FC<{ agent: Agent; onSelect?: (agent: Agent) => void }> = ({
  agent,
  onSelect
}) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: agent.id,
    data: { agent }
  });

  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
        opacity: isDragging ? 0.5 : 1
      }
    : undefined;

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className="agent-card"
      style={style}
      onClick={() => onSelect && onSelect(agent)}
    >
      <div className="agent-card-header">
        <div
          className="agent-icon"
          style={{ background: agent.color }}
        >
          {agent.icon}
        </div>
        <div className="agent-info">
          <div className="agent-name">{agent.name}</div>
          <div className="agent-category">{agent.category}</div>
        </div>
      </div>

      <p className="agent-description">{agent.description}</p>

      <div className="agent-metrics">
        <div className="metric">
          <span className="metric-label">Cost:</span>
          <span className="metric-value">${agent.estimatedCost.toFixed(3)}</span>
        </div>
        <div className="metric">
          <span className="metric-label">Duration:</span>
          <span className="metric-value">{agent.avgDuration}ms</span>
        </div>
        <div className="metric">
          <span className="metric-label">Success:</span>
          <span className="metric-value">{(agent.successRate * 100).toFixed(0)}%</span>
        </div>
      </div>
    </div>
  );
};
